// src/components/welcome/TrendingCoinsTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Typography,
  Avatar,
  LinearProgress,
} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { formatCurrency, formatChange, pctColor } from "./formatters";
import { useNavigate } from "react-router-dom";
import { getTrending } from "../coinGecko1";

export default function TrendingCoinsTable() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        const data = await getTrending(
          { vs: "usd", signal: controller.signal },
          {
            onRefresh: (fresh) => {
              if (!alive) return;
              setRows(Array.isArray(fresh) ? fresh : []);
              setLastUpdated(new Date());
            },
          }
        );
        if (!alive) return;
        setRows(Array.isArray(data) ? data : []);
        setLastUpdated(new Date());
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      clearInterval(id);
      controller.abort();
    };
  }, []);

  const displayRows = useMemo(() => rows.slice(0, 7), [rows]);

  const BINANCE_YELLOW = "#F0B90B";
  const BG_COLOR = "#263238";
  const TEXT_MUTED = "#B0BEC5";
  const BORDER_COLOR = "rgba(255,255,255,0.08)";

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${BORDER_COLOR}`,
        bgcolor: BG_COLOR,
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${BORDER_COLOR}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: "#FFFFFF",
              letterSpacing: 0.5,
            }}
          >
            Trending Coins
          </Typography>
          <Chip
            label="24h"
            size="small"
            icon={
              <ArrowOutwardIcon
                sx={{
                  color: BINANCE_YELLOW,
                  filter: "drop-shadow(0 0 3px rgba(240,185,11,0.6))",
                }}
              />
            }
            sx={{
              fontWeight: 700,
              bgcolor: "rgba(240,185,11,0.12)",
              color: BINANCE_YELLOW,
              "& .MuiChip-icon": { color: BINANCE_YELLOW },
              border: `1px solid rgba(240,185,11,0.25)`,
            }}
          />
        </Box>
        {lastUpdated && (
          <Typography
            variant="caption"
            sx={{ color: TEXT_MUTED, fontStyle: "italic" }}
          >
            Updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {loading && (
        <LinearProgress
          sx={{
            mb: 1,
            bgcolor: "rgba(255,255,255,0.05)",
            "& .MuiLinearProgress-bar": { bgcolor: BINANCE_YELLOW },
          }}
        />
      )}

      {/* Table */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          maxHeight: 360,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "#37474F",
                "& th": {
                  color: TEXT_MUTED,
                  fontWeight: 700,
                  fontSize: 13,
                  borderBottom: `1px solid ${BORDER_COLOR}`,
                },
              }}
            >
              <TableCell>Asset</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Change 24h</TableCell>
              <TableCell align="right">Market Cap</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((r) => (
              <TableRow
                key={r.id}
                hover
                onClick={() => navigate("/markets")}
                sx={{
                  cursor: "pointer",
                  transition: "background 0.2s ease, transform 0.1s ease",
                  "&:hover": {
                    background: "rgba(240,185,11,0.08)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    minWidth: 160,
                    color: "#FFFFFF",
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={r.image}
                      alt={r.symbol}
                      sx={{
                        width: 22,
                        height: 22,
                        border: `1px solid rgba(240,185,11,0.3)`,
                      }}
                    />
                    {r.symbol?.toUpperCase()}
                  </Box>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    minWidth: 120,
                    color: "#EAEFF2",
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  {formatCurrency(r.current_price, "USD")}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    minWidth: 120,
                    color: pctColor(r.price_change_percentage_24h),
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                    fontWeight: 600,
                  }}
                >
                  {formatChange(r.price_change_percentage_24h)}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    minWidth: 160,
                    color: "#EAEFF2",
                    borderBottom: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  {formatCurrency(r.market_cap, "USD")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          pt: 1,
          display: "flex",
          justifyContent: "space-between",
          borderTop: `1px solid ${BORDER_COLOR}`,
        }}
      >
        {/* <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
          * CoinGecko snapshot (SWR + backoff)
        </Typography> */}
        <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
          BTC, ETH, SOL, market volume, volatility
        </Typography>
      </Box>
    </Box>
  );
}
